import React, { useState } from 'react';

import { AISpendMeter } from '@/components/AISpendMeter';
import { Tooltip } from '@/components/Tooltip';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import {
  AI_BUDGET_CATEGORIES,
  AIBudgetCategory,
  AIBudgetStatus,
  useAIBudgetStatus,
} from '@/hooks/useAIBudget';
import { useEntitlements } from '@/hooks/useEntitlements';
import { AISpendBudgetWizard } from '@/sections/settings/components/forms/AISpendBudgetWizard';
import { SettingCompactCard } from '@/sections/settings/components/primitives/SettingCompactCard';
import { SettingDisplayCard } from '@/sections/settings/components/SettingDisplayCard';
import { useAuth } from '@/state/auth';
import { cn } from '@/utils/classname';
import { formatUSD } from '@/utils/formatCurrency.util';

interface AISpendCardProps {
  email?: string;
}

const CONTENT_RIGHT_PADDING = 'pr-24';

export const AISpendCard: React.FC<AISpendCardProps> = ({ email }) => {
  const { isPraetorianUser } = useAuth();
  const { isSuperAdmin, canManageSettings } = useEntitlements();
  const { data, isLoading, isError } = useAIBudgetStatus({ email });
  const [wizardOpen, setWizardOpen] = useState(false);

  if (isLoading) {
    return (
      <SettingDisplayCard data-testid="ai-spend-card-loading">
        <h4 className="font-bold">AI Spend</h4>
        <Spinner size="lg" />
      </SettingDisplayCard>
    );
  }

  if (isError) {
    return (
      <SettingDisplayCard data-testid="ai-spend-card-error">
        <h4 className="font-bold">AI Spend</h4>
        <span className="text-chariot-text-secondary text-xs">
          Failed to load AI spend.
        </span>
      </SettingDisplayCard>
    );
  }

  if (data === null) {
    return (
      <SettingDisplayCard data-testid="ai-spend-card-unprovisioned">
        <h4 className="font-bold">AI Spend</h4>
        <span className="text-chariot-text-secondary text-xs">
          Not provisioned yet.
        </span>
        {!isPraetorianUser && (
          <span className="text-chariot-text-secondary text-xs">
            Set by your Guard Intelligence tier.
          </span>
        )}
        <CategoryBreakdown overallLimit={0} overallUsage={0} hasLimit={false} />
      </SettingDisplayCard>
    );
  }

  if (data === undefined) {
    return null;
  }

  const hasLimit = data.set;
  const categories = data.categories;
  const canEdit = isSuperAdmin || canManageSettings;

  return (
    <>
      <SettingCompactCard data-testid="ai-spend-card">
        <div
          className={cn('flex w-full flex-col gap-4', CONTENT_RIGHT_PADDING)}
        >
          <div className="flex w-full flex-col gap-2">
            <h4 className="font-bold">AI Spend</h4>
            <AISpendMeter
              currentUsage={data.current_usage}
              maxLimit={data.max_limit}
              hasLimit={hasLimit}
            />
            {!isPraetorianUser && (
              <span className="text-chariot-text-secondary text-xs">
                Set by your Guard Intelligence tier.
              </span>
            )}
          </div>

          <CategoryBreakdown
            categories={categories}
            overallLimit={data.max_limit}
            overallUsage={data.current_usage}
            hasLimit={hasLimit}
          />
        </div>
        <div className="absolute top-1/2 right-4 -translate-y-1/2">
          {canEdit ? (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setWizardOpen(true)}
            >
              Edit
            </Button>
          ) : (
            <Tooltip title="Contact Nathan Sportsman, Peter Kwan, or Noah Tutt to update the AI budget">
              <span tabIndex={0} data-testid="edit-locked">
                <Button variant="ghost" size="sm" disabled>
                  Edit
                </Button>
              </span>
            </Tooltip>
          )}
        </div>
      </SettingCompactCard>

      {wizardOpen && (
        <AISpendBudgetWizard
          email={email}
          currentMaxLimit={hasLimit ? data.max_limit : undefined}
          categories={categories}
          canEditOverallBudget={isSuperAdmin}
          open
          onOpenChange={setWizardOpen}
        />
      )}
    </>
  );
};

const CategoryBreakdown: React.FC<{
  categories?: Partial<Record<AIBudgetCategory, AIBudgetStatus>>;
  overallLimit: number;
  overallUsage: number;
  hasLimit: boolean;
}> = ({ categories, overallLimit, overallUsage, hasLimit }) => {
  const remaining = Math.max(0, overallLimit - overallUsage);

  return (
    <div
      className="border-chariot-stroke flex flex-col gap-3 border-t pt-3"
      data-testid="category-breakdown"
    >
      <div className="flex items-center justify-between gap-4">
        <div className="flex flex-col gap-0.5">
          <span className="text-chariot-text-primary text-xs font-medium">
            Usage by category
          </span>
          <span className="text-chariot-text-secondary text-xs">
            Allocation and actual spend this month
          </span>
        </div>
        <span className="text-chariot-text-secondary shrink-0 text-xs tabular-nums">
          {hasLimit
            ? `${formatUSD(remaining)} remaining overall`
            : 'No budget set'}
        </span>
      </div>
      <div className="grid grid-cols-1 gap-2 md:grid-cols-3">
        {AI_BUDGET_CATEGORIES.map(({ key, label }) => {
          const status = categories?.[key];
          const percent =
            status && status.max_limit > 0
              ? Math.min(100, (status.current_usage / status.max_limit) * 100)
              : 0;
          const overLimit = status
            ? status.current_usage > status.max_limit
            : false;

          return (
            <div
              key={key}
              className={cn(
                'border-chariot-stroke flex flex-col gap-2 rounded-md border p-3 transition-opacity',
                !status && 'opacity-45 grayscale'
              )}
              aria-disabled={!status}
              data-testid={`category-budget-${key}-card`}
            >
              <div className="flex items-center justify-between gap-2">
                <span className="text-chariot-text-primary text-xs font-medium">
                  {label}
                </span>
                <span className="text-chariot-text-secondary text-xs tabular-nums">
                  {status ? `${percent.toFixed(0)}% used` : 'Not allocated'}
                </span>
              </div>
              <div
                className="bg-chariot-background-secondary h-1.5 overflow-hidden rounded-full"
                role="progressbar"
                aria-label={`${label} usage`}
                aria-valuenow={Math.round(percent)}
                aria-valuemin={0}
                aria-disabled={!status}
                aria-valuemax={100}
              >
                <div
                  className={cn(
                    'h-full rounded-full',
                    overLimit
                      ? 'bg-chariot-stroke-critical'
                      : 'bg-chariot-brand-primary'
                  )}
                  style={{ width: `${percent}%` }}
                  data-testid={`category-budget-${key}-bar`}
                />
              </div>
              <div className="flex items-baseline justify-between gap-2 tabular-nums">
                <span className="text-chariot-text-primary text-sm font-semibold">
                  {formatUSD(status?.current_usage ?? 0)}
                </span>
                <span className="text-chariot-text-secondary text-xs">
                  {status
                    ? `of ${formatUSD(status.max_limit)}`
                    : 'No category limit'}
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
